/**
 * Rendu en direct dans l'éditeur (design system, Editeur) : le Markdown garde l'apparence de
 * la carte en séance. Les marques (`**`, `#`, `{{`…) ne sont visibles, en texte-secondaire,
 * que sur la ligne où se trouve le curseur ; ailleurs elles sont masquées. Une formule est
 * rendue par KaTeX tant que le curseur n'y est pas ; un trou devient une pastille numérotée.
 */
import { syntaxTree } from '@codemirror/language';
import {
  StateEffect,
  StateField,
  type EditorState,
  type Extension,
  type Range,
} from '@codemirror/state';
import { Decoration, EditorView, WidgetType, type DecorationSet } from '@codemirror/view';
import katex from 'katex';
import { analyserTrous, numeroterTrous } from '../../cartes/trous';

/** Adresse affichable d'une image enregistrée. */
export type ResoudreImage = (chemin: string) => Promise<string>;

class FormuleWidget extends WidgetType {
  constructor(
    readonly latex: string,
    readonly bloc: boolean,
  ) {
    super();
  }
  override eq(autre: FormuleWidget) {
    return autre.latex === this.latex && autre.bloc === this.bloc;
  }
  toDOM() {
    const element = document.createElement(this.bloc ? 'div' : 'span');
    element.className = this.bloc ? 'fc-formule fc-formule--bloc' : 'fc-formule';
    element.innerHTML = katex.renderToString(this.latex, {
      displayMode: this.bloc,
      throwOnError: false,
      trust: false,
      strict: 'ignore',
      output: 'htmlAndMathml',
    });
    return element;
  }
  override ignoreEvent() {
    return false;
  }
}

class NumeroWidget extends WidgetType {
  constructor(readonly numero: string) {
    super();
  }
  override eq(autre: NumeroWidget) {
    return autre.numero === this.numero;
  }
  toDOM() {
    const element = document.createElement('span');
    element.className = 'fc-editeur__numero';
    element.textContent = this.numero;
    element.setAttribute('aria-label', `trou ${this.numero}`);
    return element;
  }
}

class PuceWidget extends WidgetType {
  override eq() {
    return true;
  }
  toDOM() {
    const element = document.createElement('span');
    element.className = 'fc-editeur__puce';
    element.textContent = '–';
    return element;
  }
}

class ImageWidget extends WidgetType {
  constructor(
    readonly chemin: string,
    readonly alt: string,
    readonly resoudre: ResoudreImage,
  ) {
    super();
  }
  override eq(autre: ImageWidget) {
    return autre.chemin === this.chemin && autre.alt === this.alt;
  }
  toDOM() {
    const image = document.createElement('img');
    image.className = 'fc-editeur__image';
    image.alt = this.alt;
    if (/^(https?:|data:|blob:)/.test(this.chemin)) image.src = this.chemin;
    else {
      void this.resoudre(this.chemin)
        .then((src) => {
          image.src = src;
        })
        .catch(() => undefined);
    }
    return image;
  }
  override ignoreEvent() {
    return false;
  }
}

const masquer = Decoration.replace({});
const marque = Decoration.mark({ class: 'fc-editeur__marque' });
const source = Decoration.mark({ class: 'fc-editeur__source' });

const BALISES: Record<string, Decoration> = {
  Emphasis: Decoration.mark({ tagName: 'em' }),
  StrongEmphasis: Decoration.mark({ tagName: 'strong' }),
  Strikethrough: Decoration.mark({ tagName: 'del' }),
  InlineCode: Decoration.mark({ tagName: 'code' }),
  Link: Decoration.mark({ tagName: 'a', class: 'fc-editeur__lien' }),
};

const LIGNES: Record<string, Decoration> = {
  ATXHeading1: Decoration.line({ class: 'fc-editeur__titre carte-titre-1' }),
  ATXHeading2: Decoration.line({ class: 'fc-editeur__titre carte-titre-2' }),
  ATXHeading3: Decoration.line({ class: 'fc-editeur__titre carte-titre-3' }),
  ATXHeading4: Decoration.line({ class: 'fc-editeur__titre carte-titre-3' }),
  ATXHeading5: Decoration.line({ class: 'fc-editeur__titre carte-titre-3' }),
  ATXHeading6: Decoration.line({ class: 'fc-editeur__titre carte-titre-3' }),
};
const ligneCitation = Decoration.line({ class: 'fc-editeur__citation' });
const ligneCode = Decoration.line({ class: 'fc-editeur__code' });
const ligneActive = Decoration.line({ attributes: { 'data-ligne-active': '' } });

/** Marques masquées hors de la ligne active. */
const MARQUES = new Set([
  'EmphasisMark',
  'StrikethroughMark',
  'CodeMark',
  'HeaderMark',
  'QuoteMark',
  'SurlignageMarque',
  'FormuleMarque',
  'TrouMarque',
  'LinkMark',
  'URL',
  'LinkTitle',
]);

function lignesActives(state: EditorState): Set<number> {
  const lignes = new Set<number>();
  for (const plage of state.selection.ranges) {
    const debut = state.doc.lineAt(plage.from).number;
    const fin = state.doc.lineAt(plage.to).number;
    for (let i = debut; i <= fin; i++) lignes.add(i);
  }
  return lignes;
}

function curseurDans(state: EditorState, from: number, to: number): boolean {
  return state.selection.ranges.some((r) => r.from <= to && r.to >= from);
}

function construire(
  state: EditorState,
  options: { focus: boolean; resoudre: ResoudreImage },
): DecorationSet {
  const decorations: Range<Decoration>[] = [];
  const actives = options.focus ? lignesActives(state) : new Set<number>();
  const estActive = (pos: number) => actives.has(state.doc.lineAt(pos).number);
  const texte = state.doc.toString();

  // Numéros que recevront les trous à l'enregistrement (ceux qui n'en ont pas encore).
  const numerotes = analyserTrous(numeroterTrous(texte));
  const trous = analyserTrous(texte);
  const numeroDuTrou = new Map(trous.map((t, i) => [t.debut, String(numerotes[i]?.numero ?? '')]));

  syntaxTree(state).iterate({
    enter(noeud) {
      const { name, from, to } = noeud;
      const ligne = LIGNES[name];
      if (ligne) decorations.push(ligne.range(state.doc.lineAt(from).from));
      if (name === 'Blockquote') {
        for (let n = state.doc.lineAt(from).number; n <= state.doc.lineAt(to).number; n++) {
          decorations.push(ligneCitation.range(state.doc.line(n).from));
        }
      }
      if (name === 'FencedCode' || name === 'CodeBlock') {
        for (let n = state.doc.lineAt(from).number; n <= state.doc.lineAt(to).number; n++) {
          decorations.push(ligneCode.range(state.doc.line(n).from));
        }
        // Le code garde ses marques (```) visibles et n'est pas mis en forme.
        for (const m of noeud.node.getChildren('CodeMark'))
          decorations.push(marque.range(m.from, m.to));
        const info = noeud.node.getChild('CodeInfo');
        if (info) decorations.push(marque.range(info.from, info.to));
        return false;
      }

      const balise = BALISES[name];
      if (balise && to > from) decorations.push(balise.range(from, to));

      if (name === 'Surlignage') {
        const ouvrante = state.sliceDoc(
          noeud.node.firstChild?.from ?? from,
          noeud.node.firstChild?.to ?? from,
        );
        const couleur = /^==\{(.+)\}$/.exec(ouvrante)?.[1];
        const deco = couleur?.startsWith('#')
          ? Decoration.mark({
              tagName: 'mark',
              attributes: { 'data-couleur': 'perso', style: `--fc-surligne-perso: ${couleur}` },
            })
          : Decoration.mark({
              tagName: 'mark',
              attributes: couleur && couleur !== 'jaune' ? { 'data-couleur': couleur } : {},
            });
        decorations.push(deco.range(from, to));
      }

      if (
        name === 'Formule' ||
        (name === 'FormuleBloc' && noeud.node.parent?.name !== 'Document')
      ) {
        const contenu = noeud.node.getChild('FormuleTexte');
        if (
          !(options.focus && curseurDans(state, from, to)) &&
          contenu &&
          contenu.to > contenu.from
        ) {
          decorations.push(
            Decoration.replace({
              widget: new FormuleWidget(
                state.sliceDoc(contenu.from, contenu.to),
                name === 'FormuleBloc',
              ),
            }).range(from, to),
          );
          return false;
        }
        if (contenu && contenu.to > contenu.from)
          decorations.push(source.range(contenu.from, contenu.to));
      }
      if (name === 'FormuleBloc' && noeud.node.parent?.name === 'Document') {
        const contenu = noeud.node.getChild('FormuleTexte');
        const latex = contenu ? state.sliceDoc(contenu.from, contenu.to).trim() : '';
        if (!(options.focus && curseurDans(state, from, to)) && latex !== '') {
          decorations.push(
            Decoration.replace({ widget: new FormuleWidget(latex, true), block: true }).range(
              from,
              to,
            ),
          );
          return false;
        }
        for (let n = state.doc.lineAt(from).number; n <= state.doc.lineAt(to).number; n++) {
          decorations.push(ligneCode.range(state.doc.line(n).from));
        }
        for (const m of noeud.node.getChildren('FormuleMarque'))
          decorations.push(marque.range(m.from, m.to));
        return false;
      }

      if (name === 'Trou') {
        decorations.push(Decoration.mark({ class: 'fc-editeur__trou' }).range(from, to));
        const numero = noeud.node.getChild('TrouNumero');
        const widget = Decoration.widget({
          widget: new NumeroWidget(numeroDuTrou.get(from) ?? ''),
          side: 1,
        });
        if (numero) {
          if (estActive(from))
            decorations.push(marque.range(numero.from, numero.to), widget.range(numero.to));
          else
            decorations.push(
              Decoration.replace({ widget: new NumeroWidget(numeroDuTrou.get(from) ?? '') }).range(
                numero.from,
                numero.to,
              ),
            );
        } else decorations.push(widget.range(from + 2));
        const indice = noeud.node.getChild('TrouIndice');
        if (indice) decorations.push(marque.range(indice.from, indice.to));
      }

      if (name === 'Image' && !estActive(from)) {
        const crochets = noeud.node.getChildren('LinkMark');
        const alt = crochets.length >= 2 ? state.sliceDoc(crochets[0]!.to, crochets[1]!.from) : '';
        const url = noeud.node.getChild('URL');
        if (url) {
          decorations.push(
            Decoration.replace({
              widget: new ImageWidget(state.sliceDoc(url.from, url.to), alt, options.resoudre),
            }).range(from, to),
          );
          return false;
        }
      }

      if (name === 'ListMark' && to > from) {
        const texteMarque = state.sliceDoc(from, to);
        const puce = /^[-*+]$/.test(texteMarque);
        if (puce && !estActive(from))
          decorations.push(Decoration.replace({ widget: new PuceWidget() }).range(from, to));
        else decorations.push(Decoration.mark({ class: 'fc-editeur__liste' }).range(from, to));
      }

      if (MARQUES.has(name) && to > from) {
        if (estActive(from)) decorations.push(marque.range(from, to));
        else {
          // `# ` et `> ` : l'espace qui suit la marque disparaît aussi.
          const fin =
            (name === 'HeaderMark' || name === 'QuoteMark') && state.sliceDoc(to, to + 1) === ' '
              ? to + 1
              : to;
          decorations.push(masquer.range(from, fin));
        }
      }
      return undefined;
    },
  });

  for (const n of actives) decorations.push(ligneActive.range(state.doc.line(n).from));
  return Decoration.set(decorations, true);
}

/** Extension de rendu en direct. `focus` : les marques de la ligne active ne s'affichent qu'au focus. */
export function apercuEnDirect(resoudre: ResoudreImage): Extension {
  const champ = StateField.define<DecorationSet>({
    create: (state) => construire(state, { focus: false, resoudre }),
    update(valeur, tr) {
      if (
        tr.docChanged ||
        tr.selection ||
        tr.effects.length > 0 ||
        syntaxTree(tr.state) !== syntaxTree(tr.startState)
      ) {
        return construire(tr.state, {
          focus: tr.state.field(focusChamp, false) ?? false,
          resoudre,
        });
      }
      return valeur;
    },
    provide: (f) => EditorView.decorations.from(f),
  });
  return [focusChamp, suiviFocus, champ];
}

const effetFocus = StateEffect.define<boolean>();

/** L'éditeur a-t-il le focus (les marques de la ligne active ne s'affichent qu'alors). */
const focusChamp = StateField.define<boolean>({
  create: () => false,
  update(valeur, tr) {
    for (const e of tr.effects) if (e.is(effetFocus)) return e.value;
    return valeur;
  },
});

const suiviFocus = EditorView.focusChangeEffect.of((_state, focus) => effetFocus.of(focus));
